import * as THREE from 'three';
import { text, type Locale } from '../../shared/content';
import { safeAssetPath, adAspectRatio, type AdAspect, type AdDelivery } from './useAdDelivery';
export const sponsorLabel = text('Sponsored', '赞助内容', '贊助內容', 'Patrocinado');
const bulletin = text('Neighborhood notice', '街区公告', '街區公告', 'Aviso del barrio');
const notice = text(
  'Good things grow together.',
  '一起，让街区变得更好。',
  '一起，讓街區變得更好。',
  'Las cosas buenas crecen juntas.',
);

/** An actual world-space face and low-poly frame; no DOM card is used as geometry. */
export function createBillboardStructure(
  placement: 'subway' | 'cafe',
  initialAspect: AdAspect = placement === 'cafe' ? '4:3' : '16:9',
) {
  const group = new THREE.Group();
  group.name = `ad-billboard-${placement}`;
  const y = placement === 'cafe' ? 6.6 : 5;
  group.position.set(placement === 'cafe' ? -7.1 : 2, 0, placement === 'cafe' ? -5.9 : 0.6);
  group.rotation.y = 0.61;
  const material = new THREE.MeshBasicMaterial({ color: '#f5e8cb', toneMapped: false });
  let width = 4.2 * adAspectRatio(initialAspect);
  const face = new THREE.Mesh(new THREE.PlaneGeometry(width, 4.2), material);
  face.position.set(0, y, 0.2);
  face.name = `ad-face-${placement}`;
  const frameMaterial = new THREE.MeshStandardMaterial({ color: '#386c62', roughness: 1 });
  const frame = new THREE.Mesh(new THREE.BoxGeometry(width + 0.35, 4.55, 0.25), frameMaterial);
  frame.position.set(0, y, 0);
  group.add(frame, face);
  const posts: THREE.Mesh[] = [];
  for (const x of [-1, 1]) {
    const post = new THREE.Mesh(new THREE.BoxGeometry(0.18, y, 0.2), frameMaterial);
    post.position.set(x * (width / 2 - 0.15), y / 2, 0);
    group.add(post);
    posts.push(post);
  }
  return {
    group,
    face,
    labelPoint: new THREE.Vector3(0, y + 2.35, 0.2),
    setAspect(aspect: AdAspect) {
      const nextWidth = 4.2 * adAspectRatio(aspect);
      if (width === nextWidth) return;
      width = nextWidth;
      face.geometry.dispose();
      face.geometry = new THREE.PlaneGeometry(width, 4.2);
      frame.geometry.dispose();
      frame.geometry = new THREE.BoxGeometry(width + 0.35, 4.55, 0.25);
      posts.forEach((post, i) => {
        post.position.x = (i ? 1 : -1) * (width / 2 - 0.15);
      });
    },
  };
}
function fittedLines(context: CanvasRenderingContext2D, input: string, width: number, lines: number) {
  const output: string[] = [];
  let current = '';
  let truncated = false;
  for (const char of Array.from(input.replace(/\s+/g, ' '))) {
    if (context.measureText(current + char).width > width && current) {
      output.push(current.trim());
      current = char;
    } else current += char;
    if (output.length === lines) {
      truncated = true;
      break;
    }
  }
  if (output.length < lines && current) output.push(current.trim());
  if (truncated && output.length) {
    let last = output[output.length - 1]!;
    while (last && context.measureText(last + '…').width > width) last = last.slice(0, -1);
    output[output.length - 1] = last + '…';
  }
  return output;
}
async function imageFor(path: string, signal: AbortSignal) {
  const response = await fetch(path, { credentials: 'include', signal });
  if (!response.ok) throw new Error('Advertisement image unavailable.');
  const blob = await response.blob();
  if (signal.aborted) throw new Error('Cancelled.');
  return createImageBitmap(blob);
}
async function textureFor(
  delivery: AdDelivery | null,
  locale: Locale,
  aspect: AdAspect,
  signal: AbortSignal,
) {
  const canvas = document.createElement('canvas');
  canvas.width = 1024;
  canvas.height = Math.round(1024 / adAspectRatio(aspect));
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Canvas unavailable.');
  const ad = delivery?.campaign;
  let image: ImageBitmap | undefined;
  const path = safeAssetPath(ad?.assetPath);
  if (path) {
    try {
      image = await imageFor(path, signal);
    } catch {
      if (signal.aborted) throw new Error('Cancelled.');
    }
  }
  context.fillStyle = '#f4e8ce';
  context.fillRect(0, 0, 1024, canvas.height);
  context.fillStyle = '#315f55';
  context.fillRect(0, 0, 1024, 104);
  context.fillStyle = '#fff4d8';
  context.font = 'bold 52px system-ui, sans-serif';
  context.fillText(ad ? sponsorLabel[locale] : bulletin[locale], 38, 70, 948);
  const imageWidth = image ? 430 : 0;
  if (image) {
    const x = 38,
      y = 130,
      width = imageWidth,
      height = canvas.height - 172;
    const scale = Math.min(width / image.width, height / image.height);
    context.fillStyle = '#e5d9bd';
    context.fillRect(x, y, width, height);
    context.drawImage(
      image,
      x + (width - image.width * scale) / 2,
      y + (height - image.height * scale) / 2,
      image.width * scale,
      image.height * scale,
    );
    image.close();
  }
  const left = imageWidth ? imageWidth + 66 : 40,
    available = 984 - left;
  context.fillStyle = '#9a4e39';
  context.font = 'bold 42px system-ui, sans-serif';
  const sponsor = ad?.sponsor || 'NEW YORK';
  const sponsorLines = fittedLines(context, sponsor, available, 2);
  sponsorLines.forEach((line, i) => context.fillText(line, left, 165 + i * 48));
  const titleY = 175 + sponsorLines.length * 48;
  context.fillStyle = '#304d43';
  context.font = 'bold 43px system-ui, sans-serif';
  fittedLines(context, ad?.title || notice[locale], available, 3).forEach((line, i) =>
    context.fillText(line, left, titleY + i * 52),
  );
  context.font = '28px system-ui, sans-serif';
  context.fillStyle = '#53694f';
  fittedLines(context, ad?.body || '', available, 2).forEach((line, i) =>
    context.fillText(line, left, canvas.height - 81 + i * 34),
  );
  if (signal.aborted) throw new Error('Cancelled.');
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 2;
  return texture;
}
export class SceneBillboard {
  readonly structure: ReturnType<typeof createBillboardStructure>;
  delivery: AdDelivery | null = null;
  ready = false;
  private textureKey = '';
  private controller: AbortController | undefined;
  private texture: THREE.Texture | undefined;
  private disposed = false;
  constructor(private placement: 'subway' | 'cafe') {
    this.structure = createBillboardStructure(placement);
  }
  update(delivery: AdDelivery | null, locale: Locale) {
    this.delivery = delivery;
    const ad = delivery?.campaign;
    const aspect = ad?.aspect || (this.placement === 'cafe' ? '4:3' : '16:9');
    const key = JSON.stringify([ad?.creativeHash || 'notice', ad?.creativeRevision, locale, aspect]);
    if (key === this.textureKey || this.disposed) return;
    this.textureKey = key;
    this.ready = false;
    this.structure.face.visible = false;
    this.structure.setAspect(aspect);
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    textureFor(delivery, locale, aspect, controller.signal)
      .then((texture) => {
        if (this.disposed || controller.signal.aborted) {
          texture.dispose();
          return;
        }
        const material = this.structure.face.material as THREE.MeshBasicMaterial;
        this.texture?.dispose();
        this.texture = texture;
        material.map = texture;
        material.color.set('#ffffff');
        material.needsUpdate = true;
        this.structure.face.visible = true;
        this.ready = true;
      })
      .catch(() => {
        if (this.disposed || controller.signal.aborted || this.controller !== controller) return;
        this.ready = false;
      });
  }
  dispose() {
    this.disposed = true;
    this.controller?.abort();
    this.texture?.dispose();
    const materials = new Set<THREE.Material>();
    this.structure.group.traverse((object) => {
      if (object instanceof THREE.Mesh) {
        object.geometry.dispose();
        const list = Array.isArray(object.material) ? object.material : [object.material];
        list.forEach((material) => materials.add(material));
      }
    });
    for (const material of materials) material.dispose();
  }
}
